import { MaturityAssessmentFilter } from '@constants/entities/project-dashboard';
import { SortDirection } from '@store/services/types';
import { ProjectDashboardFiltersStore } from '@store/zustand/project-dashboard-filters/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const initialMaturityView = (): Pick<
  ProjectDashboardFiltersStore,
  'assessmentFilter' | 'maturitySorting'
> => ({
  assessmentFilter: MaturityAssessmentFilter.All,
  maturitySorting: { gap: SortDirection.DESC },
});

const useProjectDashboardFiltersStore = create<ProjectDashboardFiltersStore>(
  (set, get) => ({
    partners: [],
    selectedPartner: null,
    ...initialMaturityView(),

    setPartners: (partners) => {
      const { selectedPartner, partners: oldPartners } = get();

      const isCurrentSelectedPartnerExist =
        !selectedPartner || partners.includes(selectedPartner);

      const isPartnerAdded = oldPartners.length < partners.length;

      set({
        partners,
        selectedPartner:
          isPartnerAdded || !isCurrentSelectedPartnerExist
            ? partners[partners.length - 1]
            : selectedPartner,
      });
    },

    removePartner: (partner) => {
      const { partners, selectedPartner } = get();

      if (!partners.includes(partner)) {
        return;
      }

      const updatedPartners = partners.filter((p) => p !== partner);
      let newSelectedPartner = selectedPartner;

      if (selectedPartner === partner) {
        newSelectedPartner =
          updatedPartners.length > 0 ? updatedPartners[0] : null;
      }

      set({
        partners: updatedPartners,
        selectedPartner: newSelectedPartner,
      });
    },

    setSelectedPartner: (partner) => {
      const currentPartners = get().partners;

      if (partner && currentPartners.includes(partner)) {
        set({ selectedPartner: partner });
      }
    },

    isSelectedPartner: (partner) => {
      const { selectedPartner } = get();
      return selectedPartner === partner;
    },

    setAssessmentFilter: (assessmentFilter) => set({ assessmentFilter }),

    setMaturitySorting: (maturitySorting) => set({ maturitySorting }),

    resetMaturityView: () => set(initialMaturityView()),

    clearDashboardFilters: () =>
      set({ partners: [], selectedPartner: null, ...initialMaturityView() }),
  }),
);

export const useProjectDashboardFilters = () =>
  useProjectDashboardFiltersStore(
    useShallow(({ partners, selectedPartner }) => ({
      partners,
      selectedPartner,
    })),
  );

export const useMaturityViewState = () =>
  useProjectDashboardFiltersStore(
    useShallow(({ assessmentFilter, maturitySorting }) => ({
      assessmentFilter,
      maturitySorting,
    })),
  );

export const useProjectDashboardActions = () =>
  useProjectDashboardFiltersStore(
    useShallow(
      ({
        setPartners,
        setSelectedPartner,
        removePartner,
        isSelectedPartner,
        setAssessmentFilter,
        setMaturitySorting,
        resetMaturityView,
        clearDashboardFilters,
      }) => ({
        setPartners,
        setSelectedPartner,
        removePartner,
        isSelectedPartner,
        setAssessmentFilter,
        setMaturitySorting,
        resetMaturityView,
        clearDashboardFilters,
      }),
    ),
  );

export default useProjectDashboardFiltersStore;
